/**
 * What a discount comes to, and whether the clerk in front of the screen may give it (spec Section 4).
 *
 * <p>
 * <strong>The same arithmetic as `DiscountPolicy` on the server, and the pair has to agree.</strong>
 * It exists for the reason `pricing.ts` exists: a concession is typed while a customer watches, and
 * the total has to move as it is typed, which a round trip per keystroke cannot do. Nothing here is
 * what gets charged — the server resolves it again, checks the ceiling again, and its answer wins.
 * If the two ever disagree, that one is right; the cases that are easy to get wrong are pinned by
 * tests on both sides.
 * </p>
 *
 * <p>
 * <strong>A percentage and a flat amount are one concession expressed two ways, and both are
 * measured as a percentage.</strong> That equivalence is the whole point of resolving before
 * checking. A ceiling written as "this clerk may give 5%" is trivially defeated by a field that also
 * accepts "₹4,000 off" if the two are validated separately — and a counter that only offered
 * percentages would be unusable, because shops round to the note.
 * </p>
 */

/** How the concession was typed. `null` in both fields means none. */
export interface DiscountInput {
  pct: number | null;
  amount: number | null;
}

export interface ResolvedDiscount {
  /** Money off, rounded to the paisa and clamped to the rental. */
  amount: number;
  /** The percentage as typed, carried so a caller can tell which kind it is. Null for a flat sum. */
  pct: number | null;
  /** What the money is as a percentage of the gross rental. **The figure the ceiling applies to.** */
  effectivePct: number;
}

/** Two places, because that is what a customer is asked to hand over. */
function money(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Six places, matching the server's `decimal(18,6)`. Anything tighter refuses a ceiling-exact
 * discount on a rounding artefact — 5% of ₹8,333 measured back is 5.000004%, and a clerk told they
 * may give 5% must be able to give 5%.
 */
function rate(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * What the concession comes to against `rental`, or null when none was asked for.
 *
 * <p>
 * Clamped to the rental at both ends. A negative discount is a surcharge wearing the wrong field's
 * name — the rate card is where a price goes up — and one larger than the rental produces a negative
 * invoice, which is a refund the shop never agreed to.
 * </p>
 *
 * <p>
 * A rental of zero gives an effective percentage of zero rather than a division by it. Nothing off
 * nothing is not 100% off, and reporting it that way would flag a ceiling breach on a booking with
 * no money in it.
 * </p>
 */
export function resolveDiscount(rental: number, input: DiscountInput): ResolvedDiscount | null {
  if (input.pct === null && input.amount === null) {
    return null;
  }

  const gross = Math.max(0, rental);

  // The percentage wins where both arrive, matching the server: a caller sending both has described
  // one concession twice, and re-deriving the money is what keeps them in step on the next amendment.
  const raw =
    input.pct !== null ? money((gross * clampPct(input.pct)) / 100) : money(input.amount ?? 0);

  const amount = Math.min(gross, Math.max(0, raw));

  return {
    amount,
    pct: input.pct === null ? null : clampPct(input.pct),
    effectivePct: gross <= 0 ? 0 : rate((amount / gross) * 100),
  };
}

/** One garment's own concession, keyed by whatever the caller identifies lines with. */
export interface DiscountLine<TKey> {
  key: TKey;
  /** The line's own rate. Its concession resolves against this and is clamped to it. */
  rate: number;
  input: DiscountInput;
}

export interface ResolvedLine<TKey> {
  key: TKey;
  amount: number;
  pct: number | null;
}

export interface ResolvedBookingDiscount<TKey> {
  /** Per garment. Lines that came to nothing are dropped. */
  lines: ResolvedLine<TKey>[];
  lineTotal: number;
  /** The booking-level share, resolved against the post-line subtotal. */
  bookingAmount: number;
  /** `lineTotal + bookingAmount` — what actually comes off the rental. */
  total: number;
  /** `total` as a percentage of the **gross** rental. What the ceiling applies to. */
  effectivePct: number;
}

/**
 * The whole concession: each garment's own, then one over the total.
 *
 * <p>
 * <strong>Lines first, then the booking rate on what is left.</strong> That is what a shop means by
 * "₹500 off the sherwani, and then ten percent off the bill" — by the time the ten percent is said,
 * the bill is already ₹500 lighter. Applying the booking percentage to the gross instead would give
 * a bigger concession than either party described, and lets the pair exceed the rental on figures
 * that each look reasonable. Mirrors `DiscountPolicy.ResolveAll`; the server is authoritative.
 * </p>
 *
 * <p>
 * <strong>The ceiling sees the combined figure, never the two levels separately.</strong> A clerk
 * capped at 5% who could give 5% per line *and* 5% over the total has a 10% limit written as 5%.
 * </p>
 */
export function resolveAll<TKey>(
  lines: DiscountLine<TKey>[],
  booking: DiscountInput,
): ResolvedBookingDiscount<TKey> {
  let gross = 0;
  const resolvedLines: ResolvedLine<TKey>[] = [];

  for (const line of lines) {
    const rate = Math.max(0, line.rate);

    gross += rate;

    // Against this garment's own rate and clamped to it, so a line can never discount its
    // neighbours — which is what keeps the subtotal below from going negative.
    const resolved = resolveDiscount(rate, line.input);

    if (resolved !== null && resolved.amount > 0) {
      resolvedLines.push({ key: line.key, amount: resolved.amount, pct: resolved.pct });
    }
  }

  const lineTotal = resolvedLines.reduce((sum, line) => sum + line.amount, 0);
  const subtotal = Math.max(0, gross - lineTotal);

  const bookingAmount = resolveDiscount(subtotal, booking)?.amount ?? 0;
  const total = lineTotal + bookingAmount;

  return {
    lines: resolvedLines,
    lineTotal,
    bookingAmount,
    total,
    // Against the gross: that is what "how much did this customer get off" means, and what a ceiling
    // is written about. Measuring the booking share against the subtotal would understate it.
    effectivePct: gross <= 0 ? 0 : rate_(total / gross),
  };
}

function rate_(fraction: number): number {
  return rate(fraction * 100);
}

/**
 * Whether a resolved concession sits inside a ceiling.
 *
 * Zero is a real ceiling and means no discount, never "unspecified" — a role created without one
 * carries 0. A discount resolving to nothing passes at any ceiling: it is not a concession, and
 * refusing it would make *clearing* one require the authority to grant one.
 */
export function isWithinCeiling(resolved: ResolvedDiscount | null, ceilingPct: number): boolean {
  if (resolved === null || resolved.amount <= 0) {
    return true;
  }

  return resolved.effectivePct <= Math.max(0, ceilingPct);
}

/** The same, for a whole concession across both levels. */
export function isTotalWithinCeiling<TKey>(
  resolved: ResolvedBookingDiscount<TKey>,
  ceilingPct: number,
): boolean {
  return resolved.total <= 0 || resolved.effectivePct <= Math.max(0, ceilingPct);
}

/** What the customer is charged for the hire. Never negative. */
export function netRental(rental: number, discount: number): number {
  return Math.max(0, rental - Math.max(0, discount));
}

/** "7.5", not "7.500000". */
export function formatPct(pct: number): string {
  return String(rate(pct));
}
